import { api } from "@/lib/api";

let deferredInstallPrompt = null;
const listeners = new Set();

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault();
    deferredInstallPrompt = event;
    listeners.forEach((fn) => fn(true));
  });
  window.addEventListener("appinstalled", () => {
    deferredInstallPrompt = null;
    listeners.forEach((fn) => fn(false));
  });
}

export function onInstallAvailable(fn) {
  listeners.add(fn);
  fn(Boolean(deferredInstallPrompt));
  return () => listeners.delete(fn);
}

export async function promptNativeInstall() {
  if (!deferredInstallPrompt) return "unavailable";
  deferredInstallPrompt.prompt();
  const choice = await deferredInstallPrompt.userChoice;
  deferredInstallPrompt = null;
  return choice?.outcome || "dismissed";
}

export function registerServiceWorker() {
  if (!("serviceWorker" in navigator)) return;
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js", { scope: "/" }).catch((error) => console.warn("Service worker registration failed", error));
  });
}

export function isStandalone() {
  return window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;
}

export function isIOS() {
  const ua = window.navigator.userAgent || "";
  return /iPhone|iPad|iPod/.test(ua) || (ua.includes("Mac") && "ontouchend" in document);
}

export function isMobileDevice() {
  const ua = window.navigator.userAgent || "";
  return /Android|iPhone|iPad|iPod|Mobile/i.test(ua) || window.matchMedia?.("(max-width: 820px) and (pointer: coarse)").matches;
}

export function mobileLanding(user) {
  if (!user) return "/login";
  const mobile = isStandalone() || isMobileDevice();
  if (user.role === "provider") return mobile ? "/provider-portal/work" : "/provider-portal";
  if (user.role === "client") return mobile ? "/portal/evaluations" : "/portal";
  return null;
}

export function pushSupport() {
  if (!window.isSecureContext) return { supported: false, reason: "Notifications need a secure (https) connection." };
  if (!("serviceWorker" in navigator) || !("PushManager" in window) || !("Notification" in window)) {
    if (isIOS() && !isStandalone()) return { supported: false, reason: "On iPhone, add TAGForce to your Home Screen first (Share → Add to Home Screen), then open it from the icon to turn on notifications.", needsInstall: true };
    return { supported: false, reason: "This browser does not support push notifications." };
  }
  return { supported: true, permission: Notification.permission };
}

function base64ToUint8Array(value) {
  const padding = "=".repeat((4 - (value.length % 4)) % 4);
  const raw = window.atob((value + padding).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from([...raw].map((char) => char.charCodeAt(0)));
}

export async function currentPushSubscription() {
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) return null;
  const registration = await navigator.serviceWorker.getRegistration("/");
  if (!registration) return null;
  return registration.pushManager.getSubscription();
}

export async function subscribeToPush() {
  const permission = await Notification.requestPermission();
  if (permission !== "granted") throw new Error(permission === "denied" ? "Notifications are blocked for TAGForce in your browser or phone settings." : "Notification permission was not granted.");
  const registration = await navigator.serviceWorker.ready;
  const { data } = await api.get("/push/public-key");
  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    try {
      subscription = await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: base64ToUint8Array(data.public_key) });
    } catch (error) {
      throw new Error(isIOS() && !isStandalone() ? "Open TAGForce from your Home Screen icon to turn on notifications." : "This browser could not register for push notifications. Try again from the installed TAGForce app.");
    }
  }
  const response = await api.post("/push/subscriptions", subscription.toJSON());
  return response.data;
}

export async function unsubscribeFromPush() {
  const subscription = await currentPushSubscription();
  if (!subscription) return null;
  const endpoint = subscription.endpoint;
  await subscription.unsubscribe().catch(() => undefined);
  const response = await api.post("/push/subscriptions/remove", { endpoint });
  return response.data;
}
